'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { HYPERLIQUID_ARB_USDC } from '@/constants/hyperliquid'
import { BASE_USDC_ADDRESS } from '@/constants/limitless'
import { USDC_NATIVE_CONTRACT_ADDRESS } from '@/constants/tokens'
import { SOLANA_USDC_MINT } from '@/hooks/kalshi'
import { useMarketAddress } from '@/hooks/wallets/use-market-address'
import { useMarketBalances } from '@/hooks/wallets/use-market-balances'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { copyAddress } from '@/utils/clipboard'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Copy, Info, RefreshCw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { http, type Address, createPublicClient, erc20Abi, formatUnits } from 'viem'
import { arbitrum } from 'viem/chains'

function truncateAddress(address: string, startChars = 6, endChars = 6): string {
	if (address.length <= startChars + endChars) {
		return address
	}
	return `${address.slice(0, startChars)}...${address.slice(-endChars)}`
}

type Chain = 'solana' | 'polygon' | 'bsc' | 'base' | 'arbitrum'
type Asset = 'usdc' | 'usdce' | 'usdt' | 'usdc-base' | 'usdc-arbitrum'

const CHAIN_OPTIONS = [
	{ value: 'solana' as Chain, label: 'Solana', image: '/logos/solana.png' },
	{
		value: 'polygon' as Chain,
		label: 'Polygon',
		image: '/logos/matic-logo.png',
	},
	{
		value: 'bsc' as Chain,
		label: 'BNB Chain',
		image: '/logos/bnb.svg',
	},
	{
		value: 'base' as Chain,
		label: 'Base',
		image: '/logos/base.svg',
	},
	{
		// Arbitrum USDC is the entry chain for Hyperliquid HIP-4. Funds sent
		// here are auto-bridged to HyperCore by the background watcher
		// (useHyperliquidAutoDeposit) — user never sees Bridge2.
		value: 'arbitrum' as Chain,
		label: 'Arbitrum',
		image: '/logos/hyperliquid.png',
	},
]

const ASSET_OPTIONS: Record<Chain, { value: Asset; label: string; image: string }[]> = {
	solana: [{ value: 'usdc' as Asset, label: 'USDC', image: '/logos/usdc.webp' }],
	polygon: [{ value: 'usdce' as Asset, label: 'USDC.e', image: '/logos/usdc.webp' }],
	bsc: [{ value: 'usdt' as Asset, label: 'USDT', image: '/logos/usdt.png' }],
	base: [{ value: 'usdc-base' as Asset, label: 'USDC', image: '/logos/usdc.webp' }],
	arbitrum: [{ value: 'usdc-arbitrum' as Asset, label: 'USDC', image: '/logos/usdc.webp' }],
}

const ARB_USDC_ADDRESS = HYPERLIQUID_ARB_USDC
const ARBITRUM_CHAIN_ID = 42161

export function ManualTransferModal() {
	const { activeModal, closeModal, openModal } = useModalStore()
	const isOpen = activeModal === 'manual-transfer'
	const { eoaAddress, solanaAddress, isPolymarketSafeResolved } = useWallet()
	const t = useTranslations('manualTransfer')

	const [selectedChain, setSelectedChain] = useState<Chain>('polygon')
	const [selectedAsset, setSelectedAsset] = useState<Asset>('usdce')

	useEffect(() => {
		if (isOpen) {
			setSelectedChain('polygon')
			setSelectedAsset('usdce')
		}
	}, [isOpen])

	const resolvedPolymarketAddress = useMarketAddress('polymarket')
	// Gate on profile resolution: before it, the resolver falls back to the
	// deposit wallet even for legacy Safe users — showing (and letting the
	// user copy) the wrong deposit address. Undefined renders "Loading...".
	const safeAddress = isPolymarketSafeResolved ? resolvedPolymarketAddress : undefined

	const { balances, results } = useMarketBalances([
		'polymarket',
		'kalshi',
		'predictfun',
		'limitless',
	])
	const polygonBalance = balances.polymarket?.cash.formatted ?? '0.00'
	const solanaBalance = balances.kalshi?.cash.formatted ?? '0.00'
	const bscBalance = balances.predictfun?.cash.amount ?? 0
	const baseBalance = balances.limitless?.cash.formatted ?? '0.00'
	const polygonLoading = results[0]?.isLoading ?? false
	const solanaLoading = results[1]?.isLoading ?? false
	const bscLoading = results[2]?.isLoading ?? false
	const baseLoading = results[3]?.isLoading ?? false

	// Arbitrum USDC EOA balance — what useHyperliquidAutoDeposit watches.
	// Read directly via viem so the modal shows the same number the
	// watcher sees (which is what auto-bridges to HL when ≥ $5).
	const arbitrumUsdcQuery = useQuery({
		queryKey: ['arb-usdc-balance', eoaAddress],
		enabled: Boolean(eoaAddress),
		refetchInterval: 12_000,
		queryFn: async () => {
			const client = createPublicClient({ chain: arbitrum, transport: http() })
			const raw = (await client.readContract({
				address: ARB_USDC_ADDRESS as Address,
				abi: erc20Abi,
				functionName: 'balanceOf',
				args: [eoaAddress as Address],
			})) as bigint
			return Number.parseFloat(formatUnits(raw, 6))
		},
	})
	const arbitrumBalance =
		arbitrumUsdcQuery.data !== undefined ? arbitrumUsdcQuery.data.toFixed(2) : '0.00'
	const arbitrumLoading = arbitrumUsdcQuery.isLoading
	const selectedResult =
		selectedChain === 'polygon'
			? results[0]
			: selectedChain === 'solana'
				? results[1]
				: selectedChain === 'bsc'
					? results[2]
					: selectedChain === 'arbitrum'
						? arbitrumUsdcQuery
						: results[3]

	const handleBack = () => {
		closeModal()
		openModal('deposit-primary')
	}

	const handleChainChange = (chain: Chain) => {
		setSelectedChain(chain)
		// Auto-select the first available asset for the chain
		setSelectedAsset(ASSET_OPTIONS[chain][0].value)
	}

	const currentAddress =
		selectedChain === 'solana'
			? solanaAddress
			: selectedChain === 'bsc'
				? eoaAddress
				: selectedChain === 'base'
					? eoaAddress
					: selectedChain === 'arbitrum'
						? eoaAddress
						: safeAddress
	const currentBalance =
		selectedChain === 'solana'
			? solanaBalance
			: selectedChain === 'bsc'
				? bscBalance.toFixed(2)
				: selectedChain === 'base'
					? baseBalance
					: selectedChain === 'arbitrum'
						? arbitrumBalance
						: polygonBalance
	const isLoading =
		selectedChain === 'solana'
			? solanaLoading
			: selectedChain === 'bsc'
				? bscLoading
				: selectedChain === 'base'
					? baseLoading
					: selectedChain === 'arbitrum'
						? arbitrumLoading
						: polygonLoading

	const explorerUrl =
		selectedChain === 'solana'
			? `https://solscan.io/account/${currentAddress}`
			: selectedChain === 'bsc'
				? `https://bscscan.com/address/${currentAddress}`
				: selectedChain === 'base'
					? `https://basescan.org/address/${currentAddress}`
					: selectedChain === 'arbitrum'
						? `https://arbiscan.io/address/${currentAddress}`
						: `https://polygonscan.com/address/${currentAddress}`

	// EIP-681 ERC-20 Transfer Format
	const polygonUsdcURI = `ethereum:${USDC_NATIVE_CONTRACT_ADDRESS}@137/transfer?address=${currentAddress}`
	const baseUsdcURI = `ethereum:${BASE_USDC_ADDRESS}@8453/transfer?address=${currentAddress}`
	const arbitrumUsdcURI = `ethereum:${ARB_USDC_ADDRESS}@${ARBITRUM_CHAIN_ID}/transfer?address=${currentAddress}`

	// Solana Pay SPL-Token Format
	const solanaUsdcURI = `solana:${currentAddress}?spl-token=${SOLANA_USDC_MINT}`

	const minDeposit =
		selectedChain === 'solana'
			? '5 USDC'
			: selectedChain === 'bsc'
				? '5 USDT'
				: selectedChain === 'base'
					? '5 USDC'
					: selectedChain === 'arbitrum'
						? '5 USDC (auto-bridged to Hyperliquid)'
						: '5 USDC.e'

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && closeModal()}>
			<DialogContent
				closeButtonClasses="size-[24px] text-white"
				closeButtonContainerClasses="top-[15px] lg:top-[20px]  lg:right-[26px] cursor-pointer"
				className="gap-[12px] rounded-3xl border border-white/10 bg-brand-dark-light p-[18px] sm:max-w-[512px] lg:gap-[24px] lg:p-[24px]"
			>
				<DialogHeader className="relative gap-[8px]">
					<Button
						variant="ghost"
						size="icon"
						className="-ml-2 -top-[7px] absolute left-0 h-8 w-8"
						onClick={handleBack}
					>
						<ArrowLeft className="size-[24px] text-brand-gray-secondary" />
					</Button>
					<DialogTitle className="text-center font-semibold text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
					<DialogDescription className="text-center text-[16px] text-brand-gray-secondary leading-[100%]">
						{t('description')}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-[12px] rounded-[12px] border border-brand-gray p-[12px] lg:gap-[24px] lg:p-[24px]">
					{/* Chain and Asset Selectors */}
					<div className="grid grid-cols-2 gap-[16px]">
						<div className="flex flex-col gap-[8px]">
							<span className=" font-normal text-[14px] text-white/75 leading-[100%]">
								{t('chain')}
							</span>
							<Select value={selectedChain} onValueChange={(val: Chain) => handleChainChange(val)}>
								<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CHAIN_OPTIONS.map(chain => (
										<SelectItem key={chain.value} value={chain.value}>
											<div className="flex items-center gap-2">
												<img
													src={chain.image}
													alt={chain.label}
													width={24}
													height={24}
													className="rounded-full"
												/>
												{chain.label}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="flex flex-col gap-[8px]">
							<span className=" font-normal text-[14px] text-white/75 leading-[100%]">
								{t('asset')}
							</span>
							<Select value={selectedAsset} onValueChange={(val: Asset) => setSelectedAsset(val)}>
								<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ASSET_OPTIONS[selectedChain].map(asset => (
										<SelectItem key={asset.value} value={asset.value}>
											<div className="flex items-center gap-2">
												<img
													src={asset.image}
													alt={asset.label}
													width={24}
													height={24}
													className="rounded-full"
												/>
												{asset.label}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					{/* Balance Display */}
					<div className="flex flex-col gap-[8] lg:gap-[16px]">
						<span className=" font-semibold text-[16px] text-white leading-[100%]">
							{t('yourBalance')}
						</span>
						<div className="flex h-[48px] items-center justify-between gap-[12px] rounded-[8px] border border-brand-gray bg-brand-dark-light py-[12px] pr-[12px] pl-[12px] leading-0">
							<span className=" font-normal text-[14px] text-white leading-[100%]">
								${currentBalance}
							</span>
							<Button
								variant="ghost"
								size="icon"
								className="size-[24px] shrink-0 cursor-pointer"
								onClick={() => selectedResult?.refetch()}
								disabled={isLoading}
							>
								<RefreshCw
									className={`size-[18px] text-brand-muted ${isLoading ? 'animate-spin' : ''}`}
								/>
							</Button>
						</div>
					</div>

					{/* Address Section */}
					<div className="flex flex-col gap-[8px] lg:gap-[16px]">
						<span className=" font-semibold text-[16px] text-white leading-[100%]">
							{t('yourAddress')}
						</span>
						{currentAddress ? (
							<div className="flex h-[48px] items-center gap-[12px] rounded-[8px] border border-brand-gray bg-brand-dark-light py-[12px] pr-[12px] pl-[12px] leading-0">
								<code className="flex-1 font-normal text-[14px] text-white leading-[100%]">
									{truncateAddress(currentAddress)}
								</code>
								<Button
									variant="ghost"
									size="icon"
									className="size-[24px] shrink-0 cursor-pointer"
									onClick={() =>
										copyAddress(
											currentAddress,
											selectedChain === 'solana'
												? 'Solana'
												: selectedChain === 'bsc'
													? 'BNB Chain'
													: selectedChain === 'base'
														? 'Base'
														: selectedChain === 'arbitrum'
															? 'Arbitrum'
															: 'Polygon'
										)
									}
								>
									<Copy strokeWidth={2} className="size-[18px] text-brand-muted" />
								</Button>
							</div>
						) : (
							<p className="text-[14px] text-brand-gray-secondary">{t('loading')}</p>
						)}
					</div>

					{/* QR Code */}
					<div className="flex items-center gap-[16px] lg:gap-[32px]">
						<div className="flex h-[100px] w-[100px] items-center justify-center rounded-[12px] bg-white p-[16px] lg:h-[160px] lg:w-[160px]">
							{currentAddress && (
								<QRCodeSVG
									value={
										selectedChain === 'solana'
											? solanaUsdcURI
											: selectedChain === 'bsc'
												? currentAddress
												: selectedChain === 'base'
													? baseUsdcURI
													: selectedChain === 'arbitrum'
														? arbitrumUsdcURI
														: polygonUsdcURI
									}
									size={168}
								/>
							)}
						</div>
						<div className="flex flex-col items-start gap-[16px]">
							<p className=" font-medium text-[14px] text-brand-gray-secondary leading-[120%]">
								{t.rich('sendFunds', {
									asset:
										selectedAsset === 'usdt'
											? 'USDT'
											: selectedAsset === 'usdce'
												? 'USDC.e'
												: 'USDC',
									chain:
										selectedChain === 'bsc'
											? 'BNB Chain'
											: selectedChain === 'solana'
												? 'Solana mainnet'
												: selectedChain === 'base'
													? 'Base'
													: selectedChain === 'arbitrum'
														? 'Arbitrum'
														: 'Polygon',
									b: chunks => <b className="text-white">{chunks}</b>,
								})}
							</p>

							<p className=" font-medium text-[14px] text-brand-gray-secondary leading-[120%]">
								{t.rich('minimum', {
									amount: minDeposit,
									b: chunks => <b className="text-white">{chunks}</b>,
								})}
							</p>
						</div>
					</div>

					{/* Info Boxes */}
					{selectedChain === 'solana' ? (
						<div className="rounded-[12px] border border-emerald-500/20 bg-emerald-500/10 p-[8px]">
							<p className=" text-[12px] text-emerald-500">{t('solanaGasSponsored')}</p>
						</div>
					) : (
						<div className="flex flex-col gap-[8px]">
							<div className="rounded-[12px] border border-amber-500/20 bg-amber-500/10 p-[8px]">
								<p className=" text-[12px] text-amber-500">
									{selectedChain === 'bsc'
										? 'Only send USDT on BNB Chain. Other tokens or networks may result in loss.'
										: selectedChain === 'base'
											? 'Only send USDC on Base network. Other tokens or networks may result in loss.'
											: selectedChain === 'arbitrum'
												? 'Only send USDC on Arbitrum One. Funds ≥ $5 auto-bridge to Hyperliquid (~4 min). Trading requires ≥ $10 per order — deposit at least $10 if you plan to place trades. Other tokens or networks may result in loss.'
												: t('polygonWarning')}
								</p>
							</div>
							{selectedChain === 'polygon' && (
								<div className="flex items-start gap-[8px] rounded-[12px] border border-blue-500/20 bg-blue-500/10 p-[8px]">
									<Info className="mt-px size-[14px] shrink-0 text-blue-400" />
									<p className="text-[12px] text-blue-400">
										{t('usdcSwapInfo')}{' '}
										<Link
											href="/emergency-swap"
											className="font-semibold underline"
											onClick={() => closeModal()}
										>
											{t('swapItHere')}
										</Link>
										.
									</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* View on Explorer */}
				<a
					href={explorerUrl}
					className="text-center font-normal text-[16px] text-brand-gray-secondary underline"
					target="_blank"
					rel="noreferrer"
				>
					{t('seeDepositHistory')}
				</a>
			</DialogContent>
		</Dialog>
	)
}
